import { Select, TextInput, Typography } from '@design-system/ui';
import { forwardRef, InputHTMLAttributes, ReactElement } from 'react';

import { getDatePatternByCountry } from '@shared/ui/components/inputs/DateInput/format';
import {
  dayPlaceholder,
  monthPlaceholder,
  yearPlaceholder,
} from '@shared/ui/components/inputs/DateInput/placeholders';
import { ErrorMessage } from '@shared/ui/components/inputs/ErrorMessage';

import { useDateInputController } from './useDateInputController';

type TUiPattern = 'dd-MM-yyyy' | 'yyyy-MM-dd' | 'MM-dd-yyyy';

interface IDateInputProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type' | 'inputMode'> {
  readonly label: string;
  readonly error?: string;
  readonly locale: string;
  readonly countryCode: string;
}

export const DateInput = forwardRef<HTMLInputElement, IDateInputProps>(
  function DateInput(props, ref) {
    const {
      error,
      label,
      required,
      readOnly = false,
      disabled,
      value,
      onChange,
      defaultValue,
      locale,
      countryCode,
      autoComplete,
      ...inputProps
    } = props;

    const { name: inputName } = inputProps;

    // Only the birthday tokens map to date subfields in the HTML spec.
    const dayAutoComplete = autoComplete === 'bday' ? 'bday-day' : autoComplete;
    const monthAutoComplete =
      autoComplete === 'bday' ? 'bday-month' : autoComplete;
    const yearAutoComplete =
      autoComplete === 'bday' ? 'bday-year' : autoComplete;

    const {
      resultDate,
      daySearch,
      monthSearch,
      yearSearch,
      handleDayBlur,
      handleDayChange,
      handleMonthChange,
      handleYearChange,
      handleYearBlur,
      monthOptions,
    } = useDateInputController({
      defaultValue,
      onChange,
      name: inputName,
      locale,
    });

    const hasError = Boolean(error);

    const dayInput = (
      <div className="max-w-20 flex-1">
        <TextInput
          key={`${inputName}-day`}
          name={`${inputName}-day`}
          label={dayPlaceholder(locale)}
          value={daySearch}
          onChange={handleDayChange}
          onBlur={handleDayBlur}
          disabled={disabled}
          readOnly={readOnly}
          maxLength={2}
          inputMode="numeric"
          autoComplete={dayAutoComplete}
          error={hasError ? '' : undefined}
        />
      </div>
    );

    const monthInput = (
      <div className="flex-1 min-w-20">
        <Select
          key={`${inputName}-month`}
          name={`${inputName}-month`}
          value={monthSearch || null}
          onValueChange={handleMonthChange}
          label={monthPlaceholder(locale)}
          options={[...monthOptions]}
          disabled={disabled}
          autoComplete={monthAutoComplete}
          error={hasError ? '' : undefined}
          placeholder={monthPlaceholder(locale)}
        />
      </div>
    );

    const yearInput = (
      <div className="max-w-30 shrink">
        <TextInput
          key={`${inputName}-year`}
          name={`${inputName}-year`}
          label={yearPlaceholder(locale)}
          value={yearSearch}
          onChange={handleYearChange}
          onBlur={handleYearBlur}
          disabled={disabled}
          readOnly={readOnly}
          maxLength={4}
          inputMode="numeric"
          autoComplete={yearAutoComplete}
          error={hasError ? '' : undefined}
        />
      </div>
    );

    const inputsByPattern: Record<TUiPattern, ReactElement[]> = {
      'dd-MM-yyyy': [dayInput, monthInput, yearInput],
      'MM-dd-yyyy': [monthInput, dayInput, yearInput],
      'yyyy-MM-dd': [yearInput, monthInput, dayInput],
    };

    const uiPattern = getDatePatternByCountry(countryCode);

    return (
      <div className="w-full">
        <fieldset className="border-0 p-0 m-0">
          <legend className="mb-2">
            <Typography
              variant="label-overline"
              color="secondary"
            >
              {label}
              {required && (
                <span className="text-alert-negative-primary ml-1">*</span>
              )}
            </Typography>
          </legend>

          <div className="flex gap-2">{inputsByPattern[uiPattern]}</div>

          <input
            {...inputProps}
            type="hidden"
            ref={ref}
            value={value || resultDate}
            readOnly
          />
        </fieldset>

        {error && <ErrorMessage message={error} />}
      </div>
    );
  },
);
